import { useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { mintApplicationId } from './mintApplicationId';

/**
 * Route entry point for "start a new application on this Smart Form".
 * Mints a fresh applicationId and hard-navigates the URL to the
 * ID-scoped route so the shell mounts against a URL that's bookmarkable,
 * refreshable, and — for AC05/AC06 on SFA-3 — uniquely identifies THIS
 * tab's application instance.
 *
 * The id is minted inside a `useState` initializer, NOT during render,
 * so React StrictMode's double-invocation in dev doesn't burn two UUIDs
 * per mount and no re-render between mint and the Router's Navigate
 * commit can silently swap the URL out from under the flow.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): the id source will move to the server
 * once real BMO Portal handoff lands. See `mintApplicationId.ts`.
 */
export function AdvisorApplicationStartPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const location = useLocation();
  const [applicationId] = useState(mintApplicationId);
  if (!templateId) return <Navigate to="/advisor/dashboard" replace />;
  // Redirect with `replace` so the browser back button skips the
  // /new URL — the advisor doesn't want to accidentally re-mint a
  // second application by tapping Back. Preserve the query string (e.g.
  // `mode` forwarded here by AdvisorEntryPage when it minted a fresh
  // application) so it isn't dropped on this hop.
  return (
    <Navigate
      to={`/advisor/workflows/${templateId}/applications/${applicationId}${location.search}`}
      replace
    />
  );
}
